// src/components/chat/Thread.jsx — styled chat surface using assistant-ui primitives.
//
// Reusable across tutor, content-editor, future tutor. The runtime
// (message store, send handler, isRunning) is provided by the caller via
// AssistantRuntimeProvider — this component only handles layout + styling.
//
// Aesthetic: matches the rest of the app — single accent, no avatars,
// no role labels. User messages right-aligned with accent-soft, agent
// left-aligned with surface-muted.
import React from 'react';
import {
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
} from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import { ArrowUp, Square } from 'lucide-react';

// AssistantMessage hides itself when there's no content yet. assistant-ui
// inserts a placeholder AssistantMessage during the in-flight state — if
// we render the bubble unconditionally, it shows up as an empty chat
// bubble (the "single dot" the user sees) AND our 3-dot indicator below
// it. Gating on content makes the placeholder vanish and only our spinner
// remains.
function AssistantMessage() {
  const status = useMessage((m) => m.status);
  const hasContent = useMessage((m) => {
    if (!m.content) return false;
    return m.content.some((p) => {
      if (p.type === 'text' && typeof p.text === 'string') {
        return p.text.trim().length > 0;
      }
      return false;
    });
  });
  // While the message is running with no text yet, render nothing — the
  // global ThreadPrimitive.If running indicator handles "thinking".
  if (status?.type === 'running' && !hasContent) return null;
  return (
    <MessagePrimitive.Root className="flex justify-start mb-3">
      {/* Markdown styling without @tailwindcss/typography — explicit
          arbitrary-property selectors for the elements MarkdownText
          actually emits: paragraphs, lists, code, code blocks, strong,
          em, links, headings. */}
      {/* Field Notes: agent replies are UNBUBBLED reading prose at measure —
          the tutor reads like a colleague writing in the margin, not an app
          emitting toasts. All color via tokens. */}
      <div className="max-w-[66ch] text-[14.5px] leading-[1.62] text-read
                      [&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0
                      [&_ul]:my-1 [&_ul]:pl-5 [&_ul]:list-disc
                      [&_ol]:my-1 [&_ol]:pl-5 [&_ol]:list-decimal
                      [&_li]:my-0.5
                      [&_strong]:font-semibold [&_strong]:text-ink [&_em]:italic
                      [&_a]:text-accent-ink [&_a]:underline [&_a]:decoration-line-strong [&_a]:underline-offset-2
                      [&_h1]:font-heading [&_h1]:text-lg [&_h1]:text-ink [&_h1]:mt-3 [&_h1]:mb-1
                      [&_h2]:font-heading [&_h2]:text-base [&_h2]:text-ink [&_h2]:mt-3 [&_h2]:mb-1
                      [&_h3]:font-heading [&_h3]:text-[15px] [&_h3]:text-ink [&_h3]:mt-2 [&_h3]:mb-1
                      [&_code]:bg-[var(--code-bg)] [&_code]:text-[var(--code-ink)] [&_code]:border [&_code]:border-[var(--code-border)] [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono
                      [&_pre]:bg-[var(--code-block-bg)] [&_pre]:text-[var(--code-block-ink)] [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:text-xs
                      [&_pre_code]:bg-transparent [&_pre_code]:border-0 [&_pre_code]:text-inherit [&_pre_code]:p-0
                      [&_blockquote]:border-l-2 [&_blockquote]:border-accent [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-mutedink">
        <MessagePrimitive.Content components={{ Text: MarkdownTextPrimitive }} />
      </div>
    </MessagePrimitive.Root>
  );
}

export default function Thread({ emptyState = null, belowMessages = null }) {
  return (
    <ThreadPrimitive.Root className="flex h-full flex-col bg-surface">
      <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto px-4 py-3">
        {emptyState && (
          <ThreadPrimitive.Empty>{emptyState}</ThreadPrimitive.Empty>
        )}
        <ThreadPrimitive.Messages
          components={{
            UserMessage: () => (
              <MessagePrimitive.Root className="flex justify-end mb-3">
                <div className="max-w-[85%] rounded-[13px] rounded-br-[4px] bg-surface-subtle border border-line px-4 py-2 text-sm text-ink whitespace-pre-wrap">
                  <MessagePrimitive.Content />
                </div>
              </MessagePrimitive.Root>
            ),
            AssistantMessage,
          }}
        />
        {/* Thinking indicator — three soft bouncing dots, our own bubble,
            shown while the runtime is running. Now that AssistantMessage
            hides itself for empty in-flight messages, this is the only
            visible feedback during a turn. */}
        <ThreadPrimitive.If running>
          <div className="flex justify-start mb-3" aria-live="polite" aria-label="Assistant is thinking">
            <div className="px-1 py-2 inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-faint rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-faint rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-faint rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </ThreadPrimitive.If>
      </ThreadPrimitive.Viewport>

      {belowMessages}
      <Composer />
    </ThreadPrimitive.Root>
  );
}

function Composer() {
  return (
    <ComposerPrimitive.Root className="flex items-end gap-2 border-t border-line bg-surface px-4 py-3">
      {/* Don't autoFocus on mount — the panel mounts on every page,
          and the browser would scroll the document down to put the
          composer in view, hiding the page's title under the header. */}
      <ComposerPrimitive.Input
        rows={1}
        placeholder="Type a message"
        className="flex-1 resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-accent max-h-32"
      />
      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send
          aria-label="Send"
          className="h-9 w-9 flex items-center justify-center rounded-lg bg-accent text-[var(--on-accent)] hover:bg-accent-hover disabled:opacity-40 transition"
        >
          <ArrowUp className="w-4 h-4" />
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
      <ThreadPrimitive.If running>
        <ComposerPrimitive.Cancel
          aria-label="Stop"
          className="h-9 w-9 flex items-center justify-center rounded-lg bg-surface-subtle border border-line text-read hover:bg-surface-alt transition"
        >
          <Square className="w-3.5 h-3.5" />
        </ComposerPrimitive.Cancel>
      </ThreadPrimitive.If>
    </ComposerPrimitive.Root>
  );
}
